/**
 * Native in-app account deletion (App Store Guideline 5.1.1(v)).
 *
 * Search: useDeleteAccount / consumeAccountDeletedFlag / requestDeleteAccount
 * — this is the only definition.
 */
import { useRouter } from 'vue-router'
import apiClient from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useNotify } from '@/composables/useNotify'
import { isNative } from '@/native/platform'

export const ACCOUNT_DELETED_FLAG = 'fsj_account_deleted'

export function consumeAccountDeletedFlag() {
  try {
    if (sessionStorage.getItem(ACCOUNT_DELETED_FLAG) === '1') {
      sessionStorage.removeItem(ACCOUNT_DELETED_FLAG)
      return true
    }
  } catch (_) { /* ignore */ }
  return false
}

function markAccountDeleted() {
  try {
    sessionStorage.setItem(ACCOUNT_DELETED_FLAG, '1')
  } catch (_) { /* ignore */ }
}

async function clearNativeUnlock() {
  if (!isNative()) return
  try {
    const { deleteCredentials } = await import('@/native/biometric')
    await deleteCredentials()
  } catch (_) { /* ignore */ }
  try {
    const { clearPasskeyLocal } = await import('@/native/passkey')
    await clearPasskeyLocal()
  } catch (_) { /* ignore */ }
  try {
    const { setBiometricDismissed } = await import('@/native/biometricEnrollment')
    setBiometricDismissed()
  } catch (_) { /* ignore */ }
}

export function useDeleteAccount() {
  const router = useRouter()
  const authStore = useAuthStore()
  const { confirm, notify } = useNotify()

  async function requestDeleteAccount() {
    if (authStore.isGodMode) {
      notify.warning('Cannot delete an account while impersonating.')
      return false
    }

    const ok = await confirm({
      title: 'Delete account?',
      message: 'You will be signed out and your account will be deleted.',
      confirmText: 'Delete account',
      type: 'error',
    })
    if (!ok) return false

    try {
      await apiClient.post('/auth/me/delete-account/')
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Could not delete the account.'
      notify.error(detail)
      return false
    }

    await clearNativeUnlock()
    markAccountDeleted()
    try {
      await authStore.logout(true)
    } catch (_) { /* local session is cleared in finally of logout */ }

    await router.replace('/login')
    return true
  }

  return { requestDeleteAccount }
}
